/**
 * O cartão da régua de dias sem contato, na aba Negócios da Configuração (11.2; FR-12, FR-61-A).
 *
 * Dois números decidem a cor de **todos** os Negócios: até quantos dias sem contato o Negócio
 * ainda é verde, e até quantos ele é amarelo. Acima disso, vermelho. É o contrapeso inteiro do
 * abandono de carteira, e é a única tela do sistema que o ajusta.
 *
 * **A cor não é calculada aqui, e nem chega até aqui.** O serviço a deriva na consulta, contra a
 * régua vigente (AD-10): este cartão lê e escreve os **cortes**, e quem pinta os cards é o quadro
 * (3.8), a tela Hoje (3.9) e a ficha (3.10). A legenda das três faixas existe para dizer o que
 * dois números significam — sem ela, "7 e 20" é um par de inteiros sem sentido.
 *
 * **A legenda segue o que está digitado — enquanto o digitado descreve uma régua.** É ela que
 * responde "o que acontece se eu mudar isto para 3", e é essa a pergunta de quem está com a mão
 * no campo. Quando os dois cortes são iguais a faixa amarela some: é a régua degenerada, que o
 * serviço aceita de propósito ("verde até N, vermelho acima"), e a legenda diz isso em vez de
 * mostrar um intervalo vazio.
 *
 * Quando o rascunho está **incompleto**, **fora da faixa** ou **invertido**, ela volta a mostrar
 * a régua salva. O invertido é o que obriga: as três linhas sairiam se contradizendo — "até 20
 * dias" no verde, "sem faixa amarela" no meio e "mais de 7 dias" no vermelho, ao mesmo tempo.
 * Uma legenda que existe para explicar não pode responder com uma contradição, e "nada mudou
 * ainda" é a resposta honesta para um rascunho que está prestes a ser recusado.
 *
 * **Este cartão fica somente-leitura para quem não tem a permissão, e é a exceção da tela.** A
 * convenção do repositório desde a 2.1 é "esconder não é autorizar": as telas mostram as ações e
 * o serviço recusa. Aqui a régua é **configuração global** — não há "a minha régua" —, e um
 * Vendedor que digitasse 3 e 7 e levasse `403` teria mexido no que ele nunca poderia mexer. Os
 * valores continuam visíveis para todo perfil, e a razão é a mesma da rota de leitura ser
 * autenticada e nada mais: quem trabalha com a cor precisa saber de onde ela sai. O que some é o
 * caminho de salvar — e o serviço recusaria de qualquer forma, com `403`.
 *
 * **A recusa do serviço aparece verbatim, e o que a pessoa digitou fica onde está.** Um `422` de
 * corte fora da faixa, ou um `403` de permissão retirada no meio da sessão, não pode apagar o
 * rascunho: quem levou a recusa precisa corrigir o número, e não redigitá-lo.
 *
 * **Depois de salvar, a régua é relida do serviço**, e não remendada com a resposta: o serviço
 * tem um ramo idempotente (reenviar os valores já gravados responde `200` sem gravar nada), e é a
 * releitura que mantém o cartão mostrando o que o banco tem.
 *
 * **Não há "Confirmar revisão" desde a 11.2** (emenda à 9.2, decisão 13 de 23/09/2026): a ação, a
 * rota `POST /regua/revisao`, o carimbo `revisado_em` e o item da prontidão saíram do produto.
 * Salvar os cortes é o único ato deste cartão.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop: o que muda é a disposição
 * dos dois campos — lado a lado no computador, empilhados no celular —, e a decisão é do
 * `useEhCelular` do shell, por `matchMedia`. Sem `@media`.
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEhCelular } from '@/shell/Shell'
import { type Regua, useConfigurarRegua, useRegua } from './api'
import './regua-de-dias.css'

/**
 * A permissão do FR-61-A que este cartão respeita, escrita uma vez.
 *
 * Ela é comparada contra a claim do JWT, que só carrega as **não negadas** — ausência é negação
 * (AD-5). Não há distinção entre `concedida` e `limitada` aqui, e não pode haver: a régua não tem
 * dono, e não existe recorte de escopo que faça sentido sobre uma linha só.
 */
const PERMISSAO_DE_CONFIGURAR = 'configurar_regua_de_dias'

/**
 * O teto de cada corte, o mesmo do serviço (`dominio/regua.py`).
 *
 * Ele existe lá por causa do `timedelta` da expressão da cor; aqui ele é o `max` do campo **e** a
 * recusa local, porque o `<form>` é `noValidate` e o `max` sozinho não recusa nada — o servidor
 * continua sendo quem decide, como no `maxLength` dos nomes de Funil.
 */
const TETO = 3650

/**
 * Quais campos uma recusa local nomeia.
 *
 * Ela é um par de sinalizadores, e não um booleano só, porque as duas recusas locais culpam
 * campos diferentes: falta de número culpa **o campo vazio** (e só ele), e a régua invertida
 * culpa **os dois** — nenhum dos dois está errado sozinho, e apontar um deles mandaria corrigir
 * o lado que talvez estivesse certo.
 *
 * Sem esta distinção, `aria-invalid` e `aria-describedby` iriam para os dois campos em toda
 * recusa, e quem usa leitor de tela ouviria "Informe um número de dias" em cima de um campo
 * preenchido, sem saber qual dos dois corrigir.
 */
interface Recusa {
  mensagem: string
  verde: boolean
  amarelo: boolean
}

const CARREGANDO = 'Carregando a régua…'

/** As palavras do protótipo v2 (28/09/2026) para o título e a nota do cartão. */
export const TITULO_DA_REGUA = 'Régua de dias sem contato'
export const NOTA_DA_REGUA =
  'Vale para a cor no funil e para o corte dos relatórios de detecção. Alterar muda a cor de ' +
  'todos os negócios na hora.'
const SALVA = 'Régua salva.'
const FALTA_NUMERO = 'Informe um número de dias.'
const INVERTIDA = 'O corte verde não pode passar do amarelo.'
const FORA_DA_FAIXA = `Cada corte é um número de dias entre 0 e ${String(TETO)}.`

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'regua-titulo'

const ID_DO_VERDE = 'regua-verde'
const ID_DO_AMARELO = 'regua-amarelo'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * "1 dia" / "n dias" — a régua de 1 dia é legítima, e "1 dias" é feio na tela.
 *
 * Exportada porque o painel do dia (3.9) escreve o **mesmo** corte na nota do KPI "No vermelho"
 * ("acima de N dias sem contato") logo acima da faixa vermelha, que o descreve por
 * `descreverAsFaixas` ("mais de N dia sem contato"). Redigitar a regra ali fazia a mesma tela
 * discordar de si mesma numa régua de um dia: `dias_para_vermelho` aceita `ge=0`, e a nota saía
 * "acima de 1 dias" ao lado da faixa dizendo "mais de 1 dia".
 */
export function dias(quantidade: number): string {
  return quantidade === 1 ? '1 dia' : `${String(quantidade)} dias`
}

/**
 * As três faixas do FR-12, descritas a partir de um par de cortes.
 *
 * Ela é uma função pura, fora do componente, porque é a única regra de negócio desta tela e
 * porque a régua degenerada (`amarelo === vermelho`) tem de ser dita, e não desenhada como um
 * intervalo vazio: o serviço a aceita de propósito, e ela significa "verde até N, vermelho
 * acima".
 *
 * Os textos são por **dia inteiro**, como o requisito: "até 5 dias", "de 6 a 15", "mais de 15" —
 * e é o mesmo número que a tela vai mostrar ao lado da cor no card do quadro.
 */
export function descreverAsFaixas(
  verdeAte: number,
  amareloAte: number,
  /*
   * O sufixo de cada intervalo. O quadro do Funil e o painel de Hoje o querem por extenso ("até 5
   * dias sem contato"); o cartão da régua já diz "sem contato" no título e escreve só "até 5
   * dias", como no protótipo v2 (28/09/2026).
   */
  sufixo = ' sem contato',
): {
  cor: 'verde' | 'amarelo' | 'vermelho'
  titulo: string
  intervalo: string
}[] {
  return [
    {
      cor: 'verde',
      titulo: 'Em dia',
      intervalo: `até ${dias(verdeAte)}${sufixo}`,
    },
    {
      cor: 'amarelo',
      titulo: 'Esfriando',
      intervalo:
        amareloAte <= verdeAte
          ? 'sem faixa amarela nesta régua'
          : `de ${String(verdeAte + 1)} a ${dias(amareloAte)}${sufixo}`,
    },
    {
      cor: 'vermelho',
      titulo: 'Abandonado',
      intervalo: `mais de ${dias(amareloAte)}${sufixo}`,
    },
  ]
}

/** Um corte, como campo numérico — ou como texto, para quem não pode configurar. */
function CorteDaRegua({
  id,
  rotulo,
  valor,
  somenteLeitura,
  ocupado,
  aoTrocar,
  recusadoPor,
}: {
  id: string
  rotulo: string
  valor: string
  somenteLeitura: boolean
  ocupado: boolean
  aoTrocar: (valor: string) => void
  /**
   * `id` da mensagem que recusou **este** campo, quando há uma.
   *
   * É o que liga a recusa ao campo que ela nomeia: sem `aria-invalid` e `aria-describedby`, quem
   * usa leitor de tela ouve "Informe um número de dias" solto e não sabe de qual dos dois cortes
   * a mensagem fala.
   */
  recusadoPor?: string | null
}) {
  if (somenteLeitura) {
    /*
     * Somente-leitura é um `<p>` com o rótulo, e não um `<input disabled>`: um campo desabilitado
     * fica fora da ordem de tabulação e alguns leitores de tela o pulam — a pessoa não saberia
     * que a régua existe. Como texto, o valor é lido normalmente.
     */
    return (
      <p className="regua__corte-lido">
        <span className="regua__rotulo">{rotulo}</span>
        <span className="regua__valor">{dias(Number(valor))}</span>
      </p>
    )
  }

  return (
    <div className="regua__campo">
      <label className="regua__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      {/* Sem a unidade ao lado (protótipo v2, 28/09/2026): o rótulo "Verde até" e o título do
          cartão já dizem que é em dias, e a legenda logo acima escreve o intervalo por extenso. */}
      <input
        id={id}
        className="regua__entrada"
        type="number"
        inputMode="numeric"
        min={0}
        max={TETO}
        step={1}
        value={valor}
        disabled={ocupado}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={recusadoPor ?? undefined}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      />
    </div>
  )
}

/**
 * O formulário, com o rascunho semeado pela régua que o serviço respondeu.
 *
 * Ele é um componente próprio pela mesma razão que as linhas de `FunisEEtapas` são: o rascunho
 * mora no estado, e a forma de re-semeá-lo quando a régua muda é **remontar**, com uma `key`
 * derivada dos dois cortes. Um `useEffect` que reescrevesse o estado faria um render em cascata a
 * cada leitura — inclusive a que o ramo idempotente do serviço devolve inalterada.
 *
 * A `key` é o par de cortes, e **não** o objeto inteiro: `atualizado_em` muda a cada gravação de
 * verdade, e uma `key` que o incluísse remontaria o formulário mesmo quando os números fossem os
 * mesmos — jogando fora, sem aviso, um rascunho que a pessoa já tivesse começado a digitar.
 *
 * **A confirmação e a recusa do serviço moram no pai, e não aqui** — e a razão é a remontagem:
 * uma gravação que muda os cortes troca a `key`, este componente nasce de novo, e um `useState`
 * de "salvou" nasceria falso junto com ele. A mensagem sairia da tela no exato instante em que
 * ela é verdade. Quem sobrevive à remontagem é o estado da **mutação**, que vive no pai.
 */
function FormularioDaRegua({
  regua,
  ehCelular,
  podeConfigurar,
  gravacao,
}: {
  regua: Regua
  ehCelular: boolean
  podeConfigurar: boolean
  gravacao: ReturnType<typeof useConfigurarRegua>
}) {
  /**
   * O rascunho é texto, e não número — e a diferença é o campo vazio.
   *
   * Um `useState<number>` obrigaria a escolher o que `""` significa no meio da digitação (apagar
   * o 5 para escrever 12 passa por vazio), e a escolha natural — zero — mandaria ao serviço uma
   * régua que ninguém pediu. Como texto, o vazio é vazio, e a recusa local o nomeia.
   */
  const [verde, setVerde] = useState(String(regua.dias_para_amarelo))
  const [amarelo, setAmarelo] = useState(String(regua.dias_para_vermelho))
  const [recusaLocal, setRecusaLocal] = useState<Recusa | null>(null)

  const idDaRecusa = 'regua-recusa'

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    // A mutação é zerada a cada envio: a confirmação e a recusa que o pai desenha falam do envio
    // que está em voo, e nunca do anterior.
    gravacao.reset()

    const cortes = [verde, amarelo].map((texto) => Number(texto.trim()))
    /*
     * A recusa é apurada **por campo**, e não pelo par: apagar "Verde até" não torna
     * "Amarelo até" inválido. `Number('')` é `0`, e é inteiro — por isso o texto vazio é
     * conferido antes, e não só o resultado da conversão.
     */
    const verdeIncompleto = verde.trim() === '' || !Number.isInteger(cortes[0])
    const amareloIncompleto = amarelo.trim() === '' || !Number.isInteger(cortes[1])
    if (verdeIncompleto || amareloIncompleto) {
      setRecusaLocal({
        mensagem: FALTA_NUMERO,
        verde: verdeIncompleto,
        amarelo: amareloIncompleto,
      })
      return
    }
    const [paraAmarelo, paraVermelho] = cortes as [number, number]
    /*
     * A faixa vem **antes** da inversão, na mesma ordem do serviço (`_conferir_a_faixa`): um par
     * como `{-1, -5}` está errado nos dois sentidos, e mandar corrigir a relação entre dois
     * números que não são dias nenhum seria a correção errada.
     *
     * Ela é local pelo mesmo motivo da inversão, e por um segundo: o `422` do serviço chega
     * achatado pelo envelope ("Os dados enviados são inválidos."), sem dizer a faixa aceita e sem
     * nomear campo nenhum. O `min`/`max` dos campos não substitui esta recusa — o `<form>` é
     * `noValidate`, e o navegador não confere nada.
     */
    const verdeForaDaFaixa = paraAmarelo < 0 || paraAmarelo > TETO
    const amareloForaDaFaixa = paraVermelho < 0 || paraVermelho > TETO
    if (verdeForaDaFaixa || amareloForaDaFaixa) {
      setRecusaLocal({
        mensagem: FORA_DA_FAIXA,
        verde: verdeForaDaFaixa,
        amarelo: amareloForaDaFaixa,
      })
      return
    }
    /*
     * A recusa da régua invertida também é local, e não só do serviço: ela é a única que a tela
     * consegue explicar **antes** da ida à rede, e é a mais fácil de cometer — trocar os dois
     * campos de lugar. O serviço a recusa de novo com `422`, e o banco com um `CHECK`; esta é a
     * primeira das três, e não a única.
     *
     * Aqui os **dois** campos são marcados, e é o único caso em que isso é correto: a recusa é
     * sobre a relação entre eles, e qualquer um dos dois pode ser o que a pessoa quis mudar.
     */
    if (paraAmarelo > paraVermelho) {
      setRecusaLocal({ mensagem: INVERTIDA, verde: true, amarelo: true })
      return
    }
    setRecusaLocal(null)
    gravacao.mutate({ dias_para_amarelo: paraAmarelo, dias_para_vermelho: paraVermelho })
  }

  /*
   * A legenda segue o rascunho **enquanto ele descreve uma régua**, e a régua salva enquanto não
   * descreve. Três condições reprovam o rascunho, e as três produziriam uma legenda que mente:
   *
   * - incompleto (campo vazio, ou não inteiro) — não há três faixas a desenhar;
   * - **fora da faixa** (corte negativo ou acima do teto) — "até -3 dias sem contato" descreve
   *   uma régua que o serviço recusa, e a legenda estaria endossando o que o salvar vai negar;
   * - **invertido** (verde acima do amarelo) — as três linhas sairiam ao mesmo tempo, e se
   *   contradizendo: "até 20 dias" no verde, "sem faixa amarela" no meio e "mais de 7 dias" no
   *   vermelho. Uma legenda que existe para responder "o que acontece se eu mudar isto para 3"
   *   não pode responder com uma contradição — e a régua invertida é justamente o rascunho que a
   *   pessoa está prestes a ver recusado.
   *
   * Nos dois casos a legenda continua mostrando o que **está valendo**, que é a resposta honesta:
   * nada mudou ainda. Ela nunca some, porque um cartão sem legenda deixaria dois números soltos.
   */
  const doRascunho = [verde, amarelo].map((texto) => Number(texto.trim()))
  const [rascunhoVerde, rascunhoAmarelo] = doRascunho as [number, number]
  const rascunhoDescreveUmaRegua =
    verde.trim() !== '' &&
    amarelo.trim() !== '' &&
    doRascunho.every((n) => Number.isInteger(n)) &&
    doRascunho.every((n) => n >= 0 && n <= TETO) &&
    rascunhoVerde <= rascunhoAmarelo
  const faixas = rascunhoDescreveUmaRegua
    ? descreverAsFaixas(rascunhoVerde, rascunhoAmarelo, '')
    : descreverAsFaixas(regua.dias_para_amarelo, regua.dias_para_vermelho, '')

  return (
    <form className="regua__formulario" onSubmit={enviar} noValidate>
      <div className={`regua__cortes${ehCelular ? ' regua__cortes--empilhados' : ''}`}>
        <CorteDaRegua
          id={ID_DO_VERDE}
          rotulo="Verde até"
          valor={verde}
          somenteLeitura={!podeConfigurar}
          ocupado={gravacao.isPending}
          recusadoPor={recusaLocal?.verde ? idDaRecusa : null}
          aoTrocar={(valor) => {
            setRecusaLocal(null)
            gravacao.reset()
            setVerde(valor)
          }}
        />
        <CorteDaRegua
          id={ID_DO_AMARELO}
          rotulo="Amarelo até"
          valor={amarelo}
          somenteLeitura={!podeConfigurar}
          ocupado={gravacao.isPending}
          recusadoPor={recusaLocal?.amarelo ? idDaRecusa : null}
          aoTrocar={(valor) => {
            setRecusaLocal(null)
            gravacao.reset()
            setAmarelo(valor)
          }}
        />
        {podeConfigurar ? (
          /* Na mesma linha dos dois cortes, à direita deles (`.regua-campos` do protótipo). */
          <div className="regua__acoes">
            <button className="regua__botao" type="submit" disabled={gravacao.isPending}>
              {gravacao.isPending ? 'Salvando…' : 'Salvar régua'}
            </button>
          </div>
        ) : null}
      </div>

      {/* A `<ul>` ganha nome próprio: há outras listas na tela de Administração, e sem ele quem
          navega por marcos não sabe em qual entrou. Ela é incondicional — `descreverAsFaixas`
          sempre devolve as três, e um cartão sem legenda deixaria dois números sem sentido. */}
      <ul className="regua__faixas" aria-label="Faixas da régua">
        {faixas.map((faixa) => (
          <li className="regua__faixa" key={faixa.cor}>
            {/* O ponto colorido é decorativo: quem não distingue as cores continua lendo o
                título e o intervalo, que dizem a mesma coisa por escrito. */}
            <span className={`regua__ponto regua__ponto--${faixa.cor}`} aria-hidden="true" />
            <span className="regua__faixa-titulo">{faixa.titulo}</span>
            <span className="regua__faixa-intervalo">{faixa.intervalo}</span>
          </li>
        ))}
      </ul>

      {recusaLocal === null ? null : (
        <p className="regua__erro" id={idDaRecusa} role="alert">
          {recusaLocal.mensagem}
        </p>
      )}

      {podeConfigurar ? null : (
        <p className="regua__ajuda">
          Só quem tem a permissão de configurar a régua pode alterá-la. Estes são os cortes que
          valem para todo o sistema.
        </p>
      )}
    </form>
  )
}


export function ReguaDeDias() {
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()
  const consulta = useRegua()
  const gravacao = useConfigurarRegua()

  /**
   * Quem pode configurar, pela claim do JWT.
   *
   * `in`, e não uma comparação com `'concedida'`: a claim só carrega as permissões **não
   * negadas** (AD-5), e `limitada` passa pela porta tanto quanto `concedida` — é o que a guarda
   * do serviço faz, e duas leituras diferentes da mesma claim fariam a tela e o serviço
   * discordarem sobre quem pode.
   *
   * Sem sessão o cartão fica somente-leitura, e não em branco: a guarda de rota não deixa nada
   * interno renderizar sem sessão, e este ramo é a defesa contra o estado que ela já impede.
   */
  const podeConfigurar = sessao !== undefined && PERMISSAO_DE_CONFIGURAR in sessao.permissoes

  const regua: Regua | undefined = consulta.data

  return (
    <section className="regua" aria-labelledby={ID_DO_TITULO}>
      <div className="regua__cartao">
        <div className="regua__cabecalho">
          <h2 className="regua__titulo" id={ID_DO_TITULO}>
            {TITULO_DA_REGUA}
          </h2>
          <span className="regua__ajuda">{NOTA_DA_REGUA}</span>
        </div>

        <div className="regua__corpo">
          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="regua__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `|| regua === undefined` fecha o terceiro estado: sem rede, a consulta fica
                **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia
                campos vazios como se a régua não tivesse cortes. */
          consulta.isLoading || regua === undefined ? (
            <p className="regua__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : (
            <>
              <FormularioDaRegua
                /* A remontagem é o que re-semeia o rascunho quando a régua muda — e a `key` é o
                   par de cortes, e não o objeto: `atualizado_em` muda a cada gravação de verdade,
                   e incluí-lo jogaria fora um rascunho em edição depois de outra pessoa salvar. */
                key={`${String(regua.dias_para_amarelo)}-${String(regua.dias_para_vermelho)}`}
                regua={regua}
                ehCelular={ehCelular}
                podeConfigurar={podeConfigurar}
                gravacao={gravacao}
              />
              {/* A recusa e a confirmação ficam **fora** do formulário, e é a remontagem que
                  obriga: salvar uma régua nova troca a `key` e o formulário nasce de novo — um
                  estado de "salvou" lá dentro sairia da tela no instante em que ele é verdade.
                  Verbatim: a recusa do serviço é a que sabe explicar o caso. */}
              {gravacao.isError ? (
                <p className="regua__erro" role="alert">
                  {mensagemDe(gravacao.error)}
                </p>
              ) : null}
              {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
                  interromper o que a pessoa estiver fazendo para dizer isso. */}
              {gravacao.isSuccess ? (
                <p className="regua__sucesso" role="status">
                  {SALVA}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
